import { useCallback, MutableRefObject } from 'react';
import { JSONContent } from '@tiptap/core';
import { Editor } from '@tiptap/react';

import { useBatchComponentUpdateContext } from '../shared/batch-component-update.context';
import {
  EditProps,
  INFO_BOX_DEFAULT_COLOUR,
  INFO_BOX_TYPENAME,
  InfoBoxStyles,
  RICHTEXT_SETTINGS,
} from './info-box.constants';
import { useRichtext } from 'approot/shared/richtext/richtext.hooks';
import { useInputFieldState } from '../component.hooks';
import ActivityBuilderRichtextToolbar from 'approot/activity-builder/activity-builder-richtext-toolbar';
import {
  BORDER_STYLE_BOX,
  BORDER_STYLE_INSTRUCTION,
  BORDER_STYLE_NONE,
  BORDER_STYLE_NOTE,
  BORDER_STYLE_SPEECH,
  BORDER_STYLE_STATEMENT,
} from 'global/constants';
import {
  COLOUR_BLUE,
  COLOUR_PURPLE,
} from 'approot/shared/colour/colour.constants';
import Richtext from 'approot/shared/richtext/richtext';
import { ReactComponent as InfoBoxCornerSvg } from './info-box-corner.svg';
import * as StyledNote from './info-box-note.style';
import * as StyledTextBox from './info-text-box.style';
import * as StyledInfoBox from './info-box.style';
import * as StyledInfoSpeech from './info-box-speech.style';
import * as StyledInfoThought from './info-box-thought.style';
import * as StyledInfoStatementBox from './info-box-statement.style';
import * as StyledInfoInstructionBox from './info-box-instruction.style';

const InfoBoxEdit = ({ id, sectionId, properties }: EditProps) => {
  const { updateComponentProperties } = useBatchComponentUpdateContext();

  const { style } = properties;

  const onChange = useCallback(
    (richtext: JSONContent) => {
      updateComponentProperties({
        input: {
          sectionId,
          componentId: id,
          componentData: {
            properties: {
              ...properties,
              richtext,
            },
          },
        },
        properties: {
          ...properties,
          richtext,
        },
        typename: INFO_BOX_TYPENAME,
      });
    },
    [id, sectionId, properties, updateComponentProperties]
  );

  const { setEditorStateToUpdate } = useInputFieldState<JSONContent>({
    onChange,
  });

  const { editor, toolbarRef, showToolbar } = useRichtext({
    onChange: json => setEditorStateToUpdate(json),
    initialValue: properties.richtext,
    settings: RICHTEXT_SETTINGS,
  });

  return (
    <>
      <ActivityBuilderRichtextToolbar
        editor={editor}
        toolbarRef={toolbarRef}
        showToolbar={showToolbar}
      />

      {editor && style?.border === BORDER_STYLE_NOTE && (
        <InfoBoxNote editor={editor} toolbarRef={toolbarRef} />
      )}

      {editor && style?.border === BORDER_STYLE_NONE && (
        <InfoBoxText style={style} editor={editor} toolbarRef={toolbarRef} />
      )}

      {editor && style?.border === BORDER_STYLE_BOX && (
        <InfoBoxColouredBox
          style={style}
          editor={editor}
          toolbarRef={toolbarRef}
        />
      )}

      {editor && style?.border === BORDER_STYLE_STATEMENT && (
        <InfoBoxStatementBox
          editor={editor}
          style={style}
          toolbarRef={toolbarRef}
        />
      )}

      {editor && style?.border === BORDER_STYLE_INSTRUCTION && (
        <InfoBoxInstructionBox
          editor={editor}
          style={style}
          toolbarRef={toolbarRef}
        />
      )}

      {editor && style?.border === BORDER_STYLE_SPEECH && (
        <InfoBoxEditSpeech
          editor={editor}
          style={style}
          toolbarRef={toolbarRef}
        />
      )}
    </>
  );
};

export default InfoBoxEdit;

type InfoBoxProps = {
  editor: Editor;
  toolbarRef: MutableRefObject<HTMLDivElement | null>;
};

export const InfoBoxText = ({
  editor,
  toolbarRef,
  style,
  isSmall,
}: InfoBoxProps & {
  style?: InfoBoxStyles;
  isSmall?: boolean;
}) => (
  <StyledInfoBox.InfoBoxContainer $textAlignment={style?.textAlignment}>
    <StyledTextBox.InfoTextBoxEdit $width={style?.width}>
      <Richtext editor={editor} toolbarRef={toolbarRef} isSmall={isSmall} />
    </StyledTextBox.InfoTextBoxEdit>
  </StyledInfoBox.InfoBoxContainer>
);

export const InfoBoxNote = ({ editor, toolbarRef }: InfoBoxProps) => (
  <StyledNote.InfoBoxFocus $isFocused={editor.isFocused}>
    <StyledNote.InfoBoxNoteCorner>
      <InfoBoxCornerSvg />
    </StyledNote.InfoBoxNoteCorner>
    <StyledNote.InfoBoxShadow />
    <StyledNote.InfoBoxNoteEdit>
      <Richtext editor={editor} toolbarRef={toolbarRef} />
    </StyledNote.InfoBoxNoteEdit>
  </StyledNote.InfoBoxFocus>
);

export const InfoBoxColouredBox = ({
  editor,
  style,
  toolbarRef,
}: InfoBoxProps & {
  style: InfoBoxStyles;
}) => (
  <StyledInfoBox.InfoBoxContainer $textAlignment={style.textAlignment}>
    <StyledInfoBox.InfoBox
      $colour={style.baseColour || INFO_BOX_DEFAULT_COLOUR}
      $width={style.width}
    >
      <Richtext editor={editor} toolbarRef={toolbarRef} />
    </StyledInfoBox.InfoBox>
  </StyledInfoBox.InfoBoxContainer>
);

export const InfoBoxStatementBox = ({
  editor,
  style,
  toolbarRef,
}: InfoBoxProps & {
  style: InfoBoxStyles;
}) => (
  <StyledInfoBox.InfoBoxContainer $textAlignment={style.textAlignment}>
    <StyledInfoStatementBox.InfoBoxStatement
      $colour={style.baseColour || COLOUR_PURPLE.base}
      $width={style.width}
    >
      <Richtext editor={editor} toolbarRef={toolbarRef} />
    </StyledInfoStatementBox.InfoBoxStatement>
  </StyledInfoBox.InfoBoxContainer>
);

export const InfoBoxInstructionBox = ({
  editor,
  style,
  toolbarRef,
}: InfoBoxProps & {
  style: InfoBoxStyles;
}) => (
  <StyledInfoBox.InfoBoxContainer $textAlignment={style.textAlignment}>
    <StyledInfoInstructionBox.InfoBoxInstruction
      $colour={style.baseColour || COLOUR_BLUE.base}
      $width={style.width}
    >
      <Richtext editor={editor} toolbarRef={toolbarRef} />
    </StyledInfoInstructionBox.InfoBoxInstruction>
  </StyledInfoBox.InfoBoxContainer>
);

export const InfoBoxEditSpeech = ({
  style,
  editor,
  toolbarRef,
}: InfoBoxProps & {
  style: InfoBoxStyles;
}) => (
  <StyledInfoSpeech.InfoBoxSpeech
    editor={editor}
    toolbarRef={toolbarRef}
    $colour={style.baseColour || INFO_BOX_DEFAULT_COLOUR}
    $showBackgroundColour={!!style.backgroundColour}
    $borderStyle={style.border || BORDER_STYLE_BOX}
    $alignment={style.arrowAlignment || undefined}
    $position={style.arrowPosition || undefined}
    $width={style.width}
    $textAlignment={style.textAlignment}
  />
);

export const InfoBoxEditThought = ({
  style,
  editor,
  toolbarRef,
}: InfoBoxProps & {
  style: InfoBoxStyles;
}) => (
  <StyledInfoThought.InfoBoxThought
    editor={editor}
    toolbarRef={toolbarRef}
    $colour={style.baseColour || INFO_BOX_DEFAULT_COLOUR}
    $showBackgroundColour={!!style.backgroundColour}
    $borderStyle={style.border || BORDER_STYLE_BOX}
    $alignment={style.arrowAlignment || undefined}
    $position={style.arrowPosition || undefined}
    $width={style.width}
    $textAlignment={style.textAlignment}
  />
);
